import { z } from "zod";

import { SeatAddonType } from "@/pages/Private/redux/subscription/subscription.api";

import { CompanySeatSummary, SeatKindSummary } from "../schema/companies";

/**
 * Pure seat maths for the admin "Manage licences" modal and the Licences column (PREC-4063).
 *
 * Three numbers make up a company's seat capacity — plan-included, paid (Stripe) and bonus — and
 * an admin edits only the last one. Everything that decides what to send, what to warn about and
 * what to show lives here so it can be tested without a store or a network.
 */

/** Mirrors `MAX_BONUS_SEATS` on the API; a typo must not hand out a thousand free seats. */
export const MAX_BONUS_SEATS = 100;

export type BonusSeatsByType = Record<SeatAddonType, number>;

/**
 * The `GET unipile/slots-available` payload, reshaped into the same per-kind summary the admin
 * list row carries. `slotsAvailable` is the *used* count — the name is a documented trap
 * (`sendingSeatAvailability.ts`), so the mapping happens exactly once, here.
 */
export interface SlotsAvailableLike {
	slotsAvailable?: number;
	linkedinBaseAmount?: number;
	linkedinsPaidAmount?: number;
	linkedinsBonusAmount?: number;
	emailsUsed?: number;
	emailBaseAmount?: number;
	emailsPaidAmount?: number;
	emailsBonusAmount?: number;
}

const kind = (used = 0, included = 0, paid = 0, bonus = 0): SeatKindSummary => ({
	used,
	included,
	paid,
	bonus,
	total: included + paid + bonus,
});

export const slotsToSeatSummary = (slots: SlotsAvailableLike): CompanySeatSummary => ({
	linkedin: kind(
		slots.slotsAvailable,
		slots.linkedinBaseAmount,
		slots.linkedinsPaidAmount,
		slots.linkedinsBonusAmount
	),
	email: kind(
		slots.emailsUsed,
		slots.emailBaseAmount,
		slots.emailsPaidAmount,
		slots.emailsBonusAmount
	),
});

/**
 * Whether a new bonus would leave the company with fewer seats than it is using. The grant still
 * saves — existing senders are never disconnected — but new sender assignments will be refused
 * until seats free up, and the admin should see that before confirming.
 */
export const wouldExceedCapacity = (summary: SeatKindSummary, newBonus: number): boolean =>
	summary.used > summary.included + summary.paid + newBonus;

/**
 * The grants to send: one per seat kind whose bonus actually changed. Saving with both values
 * unchanged is a no-op — nothing is sent, nothing is announced on Slack.
 */
export const changedBonusGrants = (
	current: BonusSeatsByType,
	next: BonusSeatsByType
): { type: SeatAddonType; bonusSeats: number }[] =>
	(["linkedin", "email"] as const)
		.filter((type) => current[type] !== next[type])
		.map((type) => ({ type, bonusSeats: next[type] }));

/**
 * `Number("")` is `0`, which would silently turn a cleared field into "revoke every bonus seat".
 * Keep it `NaN` so the schema rejects it and the save button stays disabled.
 */
export const parseBonusInput = (raw: string): number => (raw.trim() === "" ? NaN : Number(raw));

export const createBonusSeatsSchema = (message: string) => {
	const bonus = z.number({ message }).int({ message }).min(0, { message }).max(MAX_BONUS_SEATS, {
		message,
	});

	return z.object({ linkedin: bonus, email: bonus });
};

export type BonusSeatsForm = z.infer<ReturnType<typeof createBonusSeatsSchema>>;
